import { useCallback, useEffect, useRef, useState } from 'react';
import { wsUrl } from './api';
import type { ClientMessage, Dir, PublicRoomView, RoundResult, ServerMessage } from './wire';
import type { GameState } from '@pacmates/engine';
import { isTraceEnabled, traceNotSent, traceSent } from './game/trace';

// TURN-INTENT TRACE: capture-build only -- see main.tsx's TRACE_BUILD doc
// comment. Every call site below is gated `TRACE_BUILD && isTraceEnabled()`
// so a plain/prod build can dead-code-eliminate the whole branch (and the
// trace.ts imports) rather than merely leaving it present-but-inert.
const TRACE_BUILD = import.meta.env.VITE_ENABLE_TRACE === '1';

export type ConnStatus = 'connecting' | 'open' | 'reconnecting' | 'closed';

export interface Snapshot {
  status: 'lobby' | 'playing' | 'paused' | 'over';
  tick: number;
  t: number;
  state: GameState | null;
  state_hash: string | null;
  receivedAt: number;
}

export interface RoomSocket {
  connStatus: ConnStatus;
  lobby: PublicRoomView | null;
  snapshot: Snapshot | null;
  prevSnapshot: Snapshot | null;
  roundResult: RoundResult | null;
  lastError: string | null;
  clearError: () => void;
  sendReady: (ready: boolean) => void;
  sendStart: () => void;
  sendPause: () => void;
  sendResume: () => void;
  sendRematch: () => void;
  sendLeave: () => void;
  sendInput: (dir: Dir) => void;
  /** PART 1: request a display-name change. Server is the sole authority
   * (trims, enforces 1-32 visible chars, basic profanity screen -- see
   * server/src/util/validate.ts validateName) -- on success the new name
   * arrives via the next authoritative `lobby` broadcast, never applied
   * optimistically here. On rejection the server sends
   * `{type:'error', code:'rename_rejected', ...}`, surfaced through the
   * existing `lastError`/`clearError` path (same as every other server
   * error), not a second error UI. */
  sendRename: (name: string) => void;
}

const RECONNECT_DELAYS = [400, 800, 1600, 3000, 5000];

/** Owns exactly one WebSocket for one (code, token) pair, with capped
 * exponential-backoff reconnect. All authoritative game facts arrive via
 * `snapshot`/`lobby`/`roundResult` -- this hook never derives or guesses
 * any of them, it only frames messages on/off the wire (mirrors
 * server/src/node/wsServer.ts). */
export function useRoomSocket(code: string | null, token: string | null): RoomSocket {
  const [connStatus, setConnStatus] = useState<ConnStatus>('connecting');
  const [lobby, setLobby] = useState<PublicRoomView | null>(null);
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [prevSnapshot, setPrevSnapshot] = useState<Snapshot | null>(null);
  const [roundResult, setRoundResult] = useState<RoundResult | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const seqRef = useRef(0);
  const attemptRef = useRef(0);
  const closedByUsRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const snapshotRef = useRef<Snapshot | null>(null);

  useEffect(() => {
    if (!code || !token) return;
    closedByUsRef.current = false;
    attemptRef.current = 0;

    function connect(): void {
      setConnStatus((s) => (s === 'open' ? s : attemptRef.current === 0 ? 'connecting' : 'reconnecting'));
      const ws = new WebSocket(wsUrl(code!, token!, TRACE_BUILD && isTraceEnabled()));
      wsRef.current = ws;

      ws.onopen = () => {
        attemptRef.current = 0;
        setConnStatus('open');
        setLastError(null);
      };

      ws.onmessage = (ev: MessageEvent) => {
        let msg: ServerMessage;
        try {
          msg = JSON.parse(ev.data as string) as ServerMessage;
        } catch {
          return;
        }
        switch (msg.type) {
          case 'lobby':
            setLobby(msg.room);
            if (msg.room.status === 'lobby') setRoundResult(null);
            return;
          case 'snapshot': {
            const next: Snapshot = {
              status: msg.status,
              tick: msg.tick,
              t: msg.t,
              state: msg.state,
              state_hash: msg.state_hash,
              receivedAt: performance.now(),
            };
            setPrevSnapshot(snapshotRef.current);
            snapshotRef.current = next;
            setSnapshot(next);
            return;
          }
          case 'over':
            setRoundResult(msg.result);
            return;
          case 'error':
            setLastError(msg.message);
            return;
        }
      };

      ws.onclose = () => {
        wsRef.current = null;
        if (closedByUsRef.current) {
          setConnStatus('closed');
          return;
        }
        setConnStatus('reconnecting');
        const delay = RECONNECT_DELAYS[Math.min(attemptRef.current, RECONNECT_DELAYS.length - 1)]!;
        attemptRef.current += 1;
        timerRef.current = setTimeout(connect, delay);
      };

      ws.onerror = () => {
        // 'close' always follows; nothing else to do here.
      };
    }

    connect();

    return () => {
      closedByUsRef.current = true;
      if (timerRef.current) clearTimeout(timerRef.current);
      wsRef.current?.close(1000, 'unmount');
      wsRef.current = null;
      seqRef.current = 0;
      setConnStatus('closed');
      setLobby(null);
      setSnapshot(null);
      setPrevSnapshot(null);
      snapshotRef.current = null;
      setRoundResult(null);
    };
  }, [code, token]);

  // Returns whether the frame was actually written to the socket -- used by
  // sendInput below for TURN-INTENT TRACE's SENT/NOT_SENT distinction. Every
  // OTHER call site (ready/start/pause/...) ignores the return value,
  // exactly as before this field existed.
  const send = useCallback((msg: ClientMessage): boolean => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;
    ws.send(JSON.stringify(msg));
    return true;
  }, []);

  const sendReady = useCallback((ready: boolean) => send({ type: 'ready', ready }), [send]);
  const sendStart = useCallback(() => send({ type: 'start' }), [send]);
  const sendPause = useCallback(() => send({ type: 'pause' }), [send]);
  const sendResume = useCallback(() => send({ type: 'resume' }), [send]);
  const sendRematch = useCallback(() => send({ type: 'rematch' }), [send]);
  const sendLeave = useCallback(() => send({ type: 'leave' }), [send]);
  const sendRename = useCallback((name: string) => send({ type: 'rename', name }), [send]);
  const clearError = useCallback(() => setLastError(null), []);
  const sendInput = useCallback(
    (dir: Dir) => {
      const seq = seqRef.current;
      seqRef.current += 1;
      // seq is consumed here regardless of whether the frame actually goes
      // out below (matches the real, pre-existing behavior: a dropped
      // socket silently loses that input, it does not roll the counter
      // back) -- see trace.ts's NOT_SENT doc comment for why this is worth
      // tracing, not just a cosmetic detail.
      const wasSent = send({ type: 'input', seq, dir });
      if (TRACE_BUILD && isTraceEnabled()) {
        if (wasSent) traceSent(seq, dir);
        else traceNotSent('socket_not_open', dir, seq);
      }
    },
    [send],
  );

  return {
    connStatus,
    lobby,
    snapshot,
    prevSnapshot,
    roundResult,
    lastError,
    clearError,
    sendReady,
    sendStart,
    sendPause,
    sendResume,
    sendRematch,
    sendLeave,
    sendInput,
    sendRename,
  };
}
